import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import css from "./register.module.css";
import { signUp } from "../../lib/allauth";
import { fetchCSRFToken } from "../../lib/csrf";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import logo from '../../img/headerLogoOneUpsc.png';
import { trackSignupCompleted } from "../../lib/analytics";

const RegisterForm = () => {
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchCSRFToken().catch(() => {
      Swal.fire("Error", "Failed to fetch CSRF token", "error");
    });
  }, []);

  const handleSubmit = async () => {
    if (!email || !username || !password) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t("Please fill in all the fields!"),
      });
      return;
    }

    setIsSubmitting(true);
    const button = document.querySelector("#submitBtn");

    try {
      button.textContent = t("Loading...");
      await signUp({ email, username, password });

      // Track signup completed
      trackSignupCompleted('email');

      // Success animation
      if (button) {
        button.classList.add(css.success);
        button.textContent = '✓ ' + t("Registration successful");
      }

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Registration successful! Please check your email.",
      });

      setEmail("");
      setUsername("");
      setPassword("");
      setTimeout(() => {
          window.location.href = "/";
        }, 1000);
    } catch (error) {
      console.error("Registration error:", error);

      // Error animation
      if (button) {
        button.classList.add(css.error);
        button.textContent = '✗ ' + t("Registration failed");
      }

      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t(error.message) || "An error occurred during registration.",
      });

      // Reset button after 3 seconds
      setTimeout(() => {
        if (button) {
          button.classList.remove(css.error);
          button.textContent = t("signup");
        }
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={css.registrationPanel}>
      <div className={css.returnBack}>
        <Link to="/" className={css.link}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" class="mi-solid mi-arrow-back" viewBox="0 0 24 24">
  <path d="M19 11H7.83l4.88-4.88c.39-.39.39-1.03 0-1.42a.996.996 0 0 0-1.41 0l-6.59 6.59a.996.996 0 0 0 0 1.41l6.59 6.59a.996.996 0 1 0 1.41-1.41L7.83 13H19c.55 0 1-.45 1-1s-.45-1-1-1"/>
</svg>
        </Link>
      </div>
      <div className={css.logoSection}>
        <a href="/" className={css.logoWrapper}>
            <img src={logo} alt="logo" />
        </a>
        <h2 className={css.brandTitle}>AI LINGUA</h2>
        <p className={css.brandSubtitle}>Speak The World</p>
      </div>

      <h1 className={css.title}>{t("signup")}</h1>
      <p className={css.formDescription}>{t("Create your account to get started")}</p>

      <div>
        <div className={css.formGroup}>
          <label className={css.label}>Email</label>
          <input
            type="email"
            className={css.input}
            placeholder="example@gmail.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className={css.formGroup}>
          <label className={css.label}>{t("username")}</label>
          <input
            className={css.input}
            placeholder={t("Choose a username")}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>

        <div className={css.formGroup}>
          <label className={css.label}>{t("password")}</label>
          <div className={css.passwordWrapper}>
            <input
              type={showPassword ? "text" : "password"}
              className={css.input}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <span
              className={css.eyeIcon}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>
        </div>

        <button
          type="button"
          className={`${css.submitBtn} ${css.reg}`}
          onClick={handleSubmit}
          disabled={isSubmitting || !email || !username || !password}
          id="submitBtn"
        >
          {isSubmitting ? t("loading") : t("signup")}
        </button>

        <p className={css.loginPrompt}>
          {t("haveac")}{" "}
          <Link to="/signin" className={css.link}>
            {t("signin")}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterForm;